import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle2, Circle, AlertTriangle, FileText, Shield, Building2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ChecklistItem {
  id: string;
  category: string;
  title: string;
  description: string;
  why_needed: string;
  responsible: string;
  documentation_required: string[];
  estimated_time: string;
  priority: "critical" | "high" | "medium";
  status: "not_started" | "in_progress" | "completed";
  completed_at?: string;
  completed_by?: string;
  notes?: string;
}

const CHECKLIST_ITEMS: Omit<ChecklistItem, "status" | "completed_at" | "completed_by" | "notes">[] = [
  // Legal Documents
  {
    id: "terms_conditions",
    category: "Legal",
    title: "Terms & Conditions Finalised",
    description: "Complete, reviewed, and published Terms & Conditions",
    why_needed: "Legal requirement for all users. Protects company from liability.",
    responsible: "Amanda (Legal/DPO)",
    documentation_required: ["Signed legal review", "Published T&Cs link"],
    estimated_time: "2-3 days",
    priority: "critical"
  },
  {
    id: "privacy_policy",
    category: "Legal",
    title: "Privacy Policy Published",
    description: "GDPR/CCPA compliant Privacy Policy live on site",
    why_needed: "Legal requirement in EU/UK/California. GDPR Article 13.",
    responsible: "Amanda (Legal/DPO)",
    documentation_required: ["GDPR compliance checklist", "CCPA compliance checklist", "Published policy link"],
    estimated_time: "2-3 days",
    priority: "critical"
  },
  {
    id: "ai_disclaimer",
    category: "Legal",
    title: "AI Therapy Disclaimer Active",
    description: "Clear disclaimer that AI is not a replacement for licensed therapy",
    why_needed: "Liability protection. Required for health/wellness apps.",
    responsible: "Amanda (Legal/DPO)",
    documentation_required: ["User acceptance logs", "Disclaimer text approved"],
    estimated_time: "1 day",
    priority: "critical"
  },
  {
    id: "crisis_disclaimer",
    category: "Legal",
    title: "Crisis Resources Disclaimer",
    description: "Emergency contact info and crisis line numbers displayed",
    why_needed: "User safety. Liability protection for mental health app.",
    responsible: "Amanda (Clinical Lead)",
    documentation_required: ["Crisis resource list", "Display implementation"],
    estimated_time: "1 day",
    priority: "critical"
  },
  
  // GDPR/Data Protection
  {
    id: "dpia_completed",
    category: "GDPR",
    title: "DPIA Completed",
    description: "Data Protection Impact Assessment fully completed",
    why_needed: "GDPR Article 35 - required for high-risk processing (health data, AI)",
    responsible: "Amanda (DPO)",
    documentation_required: ["Completed DPIA_COMPLETE_THIS.md", "Risk mitigation plan"],
    estimated_time: "3-5 days",
    priority: "critical"
  },
  {
    id: "ropa_maintained",
    category: "GDPR",
    title: "Record of Processing Activities (ROPA)",
    description: "All data processing activities documented",
    why_needed: "GDPR Article 30 - mandatory for all controllers",
    responsible: "Amanda (DPO)",
    documentation_required: ["ROPA spreadsheet/document"],
    estimated_time: "2-3 days",
    priority: "critical"
  },
  {
    id: "dpa_anthropic",
    category: "GDPR",
    title: "DPA with Anthropic (Claude AI)",
    description: "Data Processing Agreement signed with AI provider",
    why_needed: "GDPR Article 28 - required for all processors handling user data",
    responsible: "Amanda (DPO)",
    documentation_required: ["Signed DPA", "BAA if applicable"],
    estimated_time: "5-10 days",
    priority: "critical"
  },
  {
    id: "dpa_supabase",
    category: "GDPR",
    title: "DPA with Supabase",
    description: "Data Processing Agreement with database provider",
    why_needed: "GDPR Article 28 - Supabase stores all user data",
    responsible: "Amanda (DPO)",
    documentation_required: ["Signed DPA"],
    estimated_time: "3-5 days",
    priority: "critical"
  },
  {
    id: "dpa_stripe",
    category: "GDPR",
    title: "DPA with Stripe",
    description: "Data Processing Agreement for payment processing",
    why_needed: "GDPR Article 28 - Stripe processes payment data",
    responsible: "Amanda (Finance/DPO)",
    documentation_required: ["Signed DPA"],
    estimated_time: "3-5 days",
    priority: "high"
  },
  
  // Insurance
  {
    id: "professional_indemnity",
    category: "Insurance",
    title: "Professional Indemnity Insurance",
    description: "Coverage for professional advice/services",
    why_needed: "Protects against claims of negligent advice. Industry standard for health/wellness.",
    responsible: "Amanda (Finance)",
    documentation_required: ["Policy certificate", "Coverage £2M+ recommended"],
    estimated_time: "1-2 weeks",
    priority: "critical"
  },
  {
    id: "public_liability",
    category: "Insurance",
    title: "Public Liability Insurance",
    description: "General business liability coverage",
    why_needed: "Standard business insurance. Required by most contracts.",
    responsible: "Amanda (Finance)",
    documentation_required: ["Policy certificate", "Coverage £5M+ recommended"],
    estimated_time: "1-2 weeks",
    priority: "high"
  },
  {
    id: "cyber_insurance",
    category: "Insurance",
    title: "Cyber Liability Insurance",
    description: "Data breach and cyber incident coverage",
    why_needed: "Handles health data. GDPR breach fines can be massive (up to €20M or 4% revenue).",
    responsible: "Amanda (Finance)",
    documentation_required: ["Policy certificate", "Breach response plan"],
    estimated_time: "2-3 weeks",
    priority: "high"
  },
  
  // Technical/Security
  {
    id: "data_encryption",
    category: "Security",
    title: "Data Encryption Verified",
    description: "All user data encrypted at rest and in transit",
    why_needed: "GDPR Article 32 - security requirement. Industry best practice.",
    responsible: "Tech Team",
    documentation_required: ["Security audit report", "Encryption verification"],
    estimated_time: "1 day (verify)",
    priority: "critical"
  },
  {
    id: "backup_recovery",
    category: "Security",
    title: "Backup & Recovery Tested",
    description: "Data backup and disaster recovery procedures tested",
    why_needed: "GDPR Article 32 - availability and resilience. Business continuity.",
    responsible: "Tech Team",
    documentation_required: ["Test results", "Recovery time objectives"],
    estimated_time: "2-3 days",
    priority: "high"
  },
  {
    id: "access_controls",
    category: "Security",
    title: "Access Controls Implemented",
    description: "Role-based access, 2FA available, audit logging active",
    why_needed: "GDPR Article 32 - security measures. Prevent unauthorized access.",
    responsible: "Tech Team",
    documentation_required: ["Access control documentation", "Audit log review"],
    estimated_time: "1 day (verify)",
    priority: "critical"
  },
  {
    id: "penetration_test",
    category: "Security",
    title: "Security Penetration Test",
    description: "Professional security assessment completed",
    why_needed: "Identify vulnerabilities. Required by many insurance policies and enterprise clients.",
    responsible: "Tech Team (External vendor)",
    documentation_required: ["Pen test report", "Remediation evidence"],
    estimated_time: "2-3 weeks",
    priority: "medium"
  },
  
  // Operational
  {
    id: "safety_protocol",
    category: "Operations",
    title: "Safety Investigation Protocol Active",
    description: "SAFETY_INVESTIGATION_SOP.md implemented and tested",
    why_needed: "User safety. Legal duty of care. Regulatory compliance.",
    responsible: "Amanda (Clinical Lead)",
    documentation_required: ["SOP documentation", "Test investigation completed"],
    estimated_time: "1-2 days",
    priority: "critical"
  },
  {
    id: "crisis_response",
    category: "Operations",
    title: "Crisis Response System Tested",
    description: "Crisis detection, escalation, and resource provision working",
    why_needed: "User safety. Immediate risk management for mental health app.",
    responsible: "Amanda (Clinical Lead)",
    documentation_required: ["Test cases passed", "Crisis resource list verified"],
    estimated_time: "2-3 days",
    priority: "critical"
  },
  {
    id: "complaint_process",
    category: "Operations",
    title: "Complaint Handling Process",
    description: "User complaint submission, tracking, and resolution process live",
    why_needed: "Regulatory requirement. Customer service. Early warning system for issues.",
    responsible: "Amanda (Operations)",
    documentation_required: ["Complaint handling policy", "Response time SLAs"],
    estimated_time: "1-2 days",
    priority: "high"
  },
  {
    id: "content_moderation",
    category: "Operations",
    title: "Content Moderation Policy",
    description: "Policy for handling user-generated content and community posts",
    why_needed: "Legal liability for harmful content. Platform safety.",
    responsible: "Amanda (Operations)",
    documentation_required: ["Moderation policy", "Reporting mechanism"],
    estimated_time: "1-2 days",
    priority: "high"
  },
  
  // Financial
  {
    id: "payment_processing",
    category: "Financial",
    title: "Payment Processing Compliant",
    description: "Stripe setup, refund policy, subscription management tested",
    why_needed: "Consumer protection laws. Payment provider requirements.",
    responsible: "Amanda (Finance)",
    documentation_required: ["Refund policy published", "Payment flow tested"],
    estimated_time: "2-3 days",
    priority: "high"
  },
  {
    id: "tax_registration",
    category: "Financial",
    title: "Tax Registration Complete",
    description: "VAT registration, tax reporting setup",
    why_needed: "Legal requirement. Tax compliance.",
    responsible: "Amanda (Finance/Accountant)",
    documentation_required: ["VAT number", "Tax ID"],
    estimated_time: "1-2 weeks",
    priority: "critical"
  },
  
  // Business Setup
  {
    id: "company_registration",
    category: "Business",
    title: "Company Properly Registered",
    description: "Business entity registered with Companies House (UK) or equivalent",
    why_needed: "Legal requirement to operate. Limited liability protection.",
    responsible: "Amanda (Legal)",
    documentation_required: ["Certificate of incorporation", "Company number"],
    estimated_time: "1-2 weeks",
    priority: "critical"
  },
  {
    id: "ico_registration",
    category: "Business",
    title: "ICO Registration (UK)",
    description: "Registered with Information Commissioner's Office as data controller",
    why_needed: "Legal requirement under UK GDPR for processing personal data.",
    responsible: "Amanda (DPO)",
    documentation_required: ["ICO registration number", "Annual fee paid"],
    estimated_time: "1 day",
    priority: "critical"
  },
  {
    id: "professional_bodies",
    category: "Business",
    title: "Professional Body Membership (if applicable)",
    description: "Membership with relevant therapy/counselling bodies",
    why_needed: "Industry credibility. May be required for insurance.",
    responsible: "Amanda (Clinical Lead)",
    documentation_required: ["Membership certificates"],
    estimated_time: "2-4 weeks",
    priority: "medium"
  }
];

export function LaunchReadinessChecklist() {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadChecklist();
  }, []);

  const loadChecklist = async () => {
    try {
      const { data, error } = await supabase
        .from('launch_checklist_items')
        .select('*')
        .order('priority', { ascending: true });

      if (error) throw error;

      // Merge with template items
      const mergedItems = CHECKLIST_ITEMS.map(template => {
        const saved = data?.find((d: any) => d.item_id === template.id);
        return {
          ...template,
          status: (saved?.status || 'not_started') as ChecklistItem['status'],
          completed_at: saved?.completed_at,
          completed_by: saved?.completed_by,
          notes: saved?.notes
        };
      });

      setItems(mergedItems as ChecklistItem[]);
    } catch (error) {
      console.error('Error loading checklist:', error);
      // Use template items if DB load fails
      setItems(CHECKLIST_ITEMS.map(t => ({ ...t, status: 'not_started' as const })));
    } finally {
      setIsLoading(false);
    }
  };

  const updateItemStatus = async (itemId: string, status: ChecklistItem['status'], notes?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('launch_checklist_items')
        .upsert({
          item_id: itemId,
          status,
          notes,
          completed_at: status === 'completed' ? new Date().toISOString() : null,
          completed_by: status === 'completed' ? user?.id : null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'item_id'
        });

      if (error) throw error;

      setItems(prev => prev.map(item => 
        item.id === itemId 
          ? { 
              ...item, 
              status, 
              notes,
              completed_at: status === 'completed' ? new Date().toISOString() : undefined,
              completed_by: status === 'completed' ? user?.id : undefined
            }
          : item
      ));

      toast.success('Status updated');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const stats = {
    total: items.length,
    completed: items.filter(i => i.status === 'completed').length,
    inProgress: items.filter(i => i.status === 'in_progress').length,
    notStarted: items.filter(i => i.status === 'not_started').length,
    critical: items.filter(i => i.priority === 'critical').length,
    criticalComplete: items.filter(i => i.priority === 'critical' && i.status === 'completed').length
  };

  const progress = (stats.completed / stats.total) * 100;
  const criticalProgress = (stats.criticalComplete / stats.critical) * 100;
  const isReadyToLaunch = stats.critical === stats.criticalComplete;

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Legal': return <FileText className="h-4 w-4" />;
      case 'GDPR': return <Shield className="h-4 w-4" />;
      case 'Insurance': return <Building2 className="h-4 w-4" />;
      case 'Security': return <Shield className="h-4 w-4" />;
      default: return <Circle className="h-4 w-4" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    const variants = {
      critical: 'destructive',
      high: 'default',
      medium: 'secondary'
    };
    return <Badge variant={variants[priority as keyof typeof variants] as any}>{priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const config = {
      completed: { variant: 'default', label: 'Completed', className: 'bg-green-500' },
      in_progress: { variant: 'secondary', label: 'In Progress', className: 'bg-yellow-500' },
      not_started: { variant: 'outline', label: 'Not Started', className: '' }
    };
    const c = config[status as keyof typeof config];
    return <Badge variant={c.variant as any} className={c.className}>{c.label}</Badge>;
  };

  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">Loading checklist...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Launch Readiness Checklist</CardTitle>
              <CardDescription>
                Complete all critical items before launching globally
              </CardDescription>
            </div>
            {isReadyToLaunch ? (
              <Badge className="bg-green-500 text-white text-lg px-4 py-2">
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Ready to Launch
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-lg px-4 py-2">
                <AlertTriangle className="h-5 w-5 mr-2" />
                Not Ready
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Overall Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Overall Progress</span>
              <span>{stats.completed} / {stats.total} items</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          {/* Critical Items Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-destructive">Critical Items (Must Complete)</span>
              <span>{stats.criticalComplete} / {stats.critical} items</span>
            </div>
            <Progress value={criticalProgress} className="h-3" />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 pt-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
              <div className="text-sm text-muted-foreground">In Progress</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{stats.notStarted}</div>
              <div className="text-sm text-muted-foreground">Not Started</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checklist Items by Category */}
      <Card>
        <CardHeader>
          <CardTitle>Checklist Items</CardTitle>
          <CardDescription>
            Review each item, update status, and add notes as you complete tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full">
            {Object.entries(groupedItems).map(([category, categoryItems]) => (
              <AccordionItem key={category} value={category}>
                <AccordionTrigger className="text-lg font-semibold">
                  <div className="flex items-center gap-3">
                    {getCategoryIcon(category)}
                    {category}
                    <Badge variant="outline">
                      {categoryItems.filter(i => i.status === 'completed').length} / {categoryItems.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-4">
                    {categoryItems.map(item => (
                      <Card key={item.id} className="border-l-4" style={{
                        borderLeftColor: item.priority === 'critical' ? 'rgb(239, 68, 68)' : 
                                       item.priority === 'high' ? 'rgb(234, 179, 8)' : 
                                       'rgb(156, 163, 175)'
                      }}>
                        <CardContent className="pt-6">
                          <div className="space-y-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Checkbox
                                    checked={item.status === 'completed'}
                                    onCheckedChange={(checked) => {
                                      updateItemStatus(
                                        item.id, 
                                        checked ? 'completed' : 'not_started'
                                      );
                                    }}
                                  />
                                  <h4 className="font-semibold">{item.title}</h4>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                                
                                <div className="bg-muted/50 p-3 rounded-md space-y-2 text-sm">
                                  <div>
                                    <strong>Why needed:</strong> {item.why_needed}
                                  </div>
                                  <div>
                                    <strong>Responsible:</strong> {item.responsible}
                                  </div>
                                  <div>
                                    <strong>Est. time:</strong> {item.estimated_time}
                                  </div>
                                  <div>
                                    <strong>Documentation:</strong>
                                    <ul className="list-disc list-inside ml-2 mt-1">
                                      {item.documentation_required.map((doc, idx) => (
                                        <li key={idx}>{doc}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex flex-col gap-2">
                                {getPriorityBadge(item.priority)}
                                {getStatusBadge(item.status)}
                              </div>
                            </div>

                            {/* Status Buttons */}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={item.status === 'not_started' ? 'default' : 'outline'}
                                onClick={() => updateItemStatus(item.id, 'not_started')}
                              >
                                Not Started
                              </Button>
                              <Button
                                size="sm"
                                variant={item.status === 'in_progress' ? 'default' : 'outline'}
                                onClick={() => updateItemStatus(item.id, 'in_progress')}
                              >
                                In Progress
                              </Button>
                              <Button
                                size="sm"
                                variant={item.status === 'completed' ? 'default' : 'outline'}
                                onClick={() => updateItemStatus(item.id, 'completed')}
                              >
                                Completed
                              </Button>
                            </div>

                            {item.completed_at && (
                              <div className="text-xs text-muted-foreground">
                                Completed: {new Date(item.completed_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
